import { z } from "zod";

/**
 * Sync protocol DTOs.
 *
 * Client: keeps an outbox of `SyncOp`s and a per-table `cursor` of the last server `rev`
 * seen. On reconnect it `push`es the outbox then `pull`s anything new.
 *
 * Server: applies `push` ops with LWW (`max(updatedAt, rev)`), assigns new `rev`s,
 * returns a fresh cursor.
 */

export const SYNC_ENTITIES = [
  "team",
  "team_coach",
  "player",
  "calendar_event",
  "formation",
  "board",
  "media_ref",
] as const;
export const syncEntitySchema = z.enum(SYNC_ENTITIES);
export type SyncEntity = z.infer<typeof syncEntitySchema>;

export const syncOpKindSchema = z.enum(["upsert", "delete"]);
export type SyncOpKind = z.infer<typeof syncOpKindSchema>;

export const syncOpSchema = z.object({
  /** Client-generated op id; lets the server dedupe retries. */
  opId: z.string().uuid(),
  entity: syncEntitySchema,
  kind: syncOpKindSchema,
  /** Full row (for upsert) or `{ id }` (for delete). */
  payload: z.record(z.string(), z.unknown()),
  /** Local logical clock — set client-side before queueing. */
  updatedAt: z.string().datetime(),
  /** Last server `rev` the client knew for this row when it wrote, if any. */
  baseRev: z.number().int().nonnegative().nullable(),
});
export type SyncOp = z.infer<typeof syncOpSchema>;

export const pushRequestSchema = z.object({
  /** Client-generated UUID identifying this writer (NOT a registered device). */
  clientId: z.string().uuid(),
  ops: z.array(syncOpSchema).max(1000),
});
export type PushRequest = z.infer<typeof pushRequestSchema>;

export const pushConflictSchema = z.object({
  opId: z.string().uuid(),
  reason: z.enum(["stale", "forbidden", "validation"]),
  serverRev: z.number().int().nonnegative().nullable(),
  /** Optional detail, e.g. "team_limit_reached" or an underlying validation error. */
  message: z.string().optional(),
});
export const pushResponseSchema = z.object({
  accepted: z.array(z.object({ opId: z.string().uuid(), rev: z.number().int().nonnegative() })),
  conflicts: z.array(pushConflictSchema),
});
export type PushResponse = z.infer<typeof pushResponseSchema>;

/** Cursor = highest `rev` the client has seen, per entity. */
export const syncCursorSchema = z.record(syncEntitySchema, z.number().int().nonnegative());
export type SyncCursor = z.infer<typeof syncCursorSchema>;

export const pullRequestSchema = z.object({
  /** Client-generated UUID identifying this writer (NOT a registered device). */
  clientId: z.string().uuid(),
  cursor: syncCursorSchema,
  /** Optional cap — server may apply its own ceiling regardless. */
  limit: z.number().int().min(1).max(2000).optional(),
});
export type PullRequest = z.infer<typeof pullRequestSchema>;

export const pullChangeSchema = z.object({
  entity: syncEntitySchema,
  rev: z.number().int().nonnegative(),
  row: z.record(z.string(), z.unknown()),
});
export const pullResponseSchema = z.object({
  changes: z.array(pullChangeSchema),
  nextCursor: syncCursorSchema,
  /** When true, client should call pull again immediately. */
  hasMore: z.boolean(),
});
export type PullResponse = z.infer<typeof pullResponseSchema>;
